/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import WebsiteAdmin from './admin/websiteAdmin/WebsiteAdmin.js'
import ObjectTypes from './admin/objectTypes/ObjectTypes.js'
import ApiErrorLog from './admin/ApiErrorLog.js'
import KeywordGroups from './keywords/KeywordGroups.js'
import UserManagement from './userManagement/UserManagement.js'
import UserStreamingHistory from './userManagement/UserStreamingHistory.js'
import UserGatewayHistory from './userManagement/UserGatewayHistory.js'
import VaultView from './vaultView/VaultView.js'
import PathParser from './pathParser/PathParser.js'
import Login from './login/Login.js'
import EnterAuthCode from './login/EnterAuthCode'
import Signup from './login/Signup.js'
import MfaLogin from './widgets/MfaLogin.js'
import RecoverAccount from './login/RecoverAccount.js'
import ResetPassword from './login/ResetPassword.js'
import ConfirmSignup from './login/ConfirmSignup.js'
import SessionLimitExceeded from './login/SessionLimitExceeded.js';
import UserAdmin from './admin/userAdmin/UserAdmin';
import SupportInfo from './help/SupportInfo.js'
import EmailControlPanel from './email/EmailControlPanel';
import ChangePassword from './userManagement/ChangePassword';
import SecureWeblink from './widgets/SecureWeblink';
import { AppContext } from './AppContext'

class AppRoutes extends React.Component
{
  activeSessionRoute({component: Component})
  {
    switch (this.props.sessionStatus)
    {
      case 'active':
        return (<Component />);

      case 'unknown':
        return (null);

      default:
        return (<Navigate to={{pathname: '/login', replace: true}} />);
    }
  }

  administratorRoute({ component: Component})
  {
    if (!this.context.userSession.isAdministrator()) return (<Navigate to={{pathname: '/login', replace: true}} />);

    switch (this.props.sessionStatus)
    {
      case 'active':
        return (<Component />);

      case 'unknown':
        return (null);

      default:
        return (<Navigate to={{pathname: '/login', replace: true}} />);
    }
  }

  gmailReplyRoute()
  {
    if (!this.context.userSession.isAdministrator()) return (<Navigate to={{pathname: '/login', replace: true}} />);
    switch (this.props.sessionStatus)
    {
      case 'active':
        return (<EmailControlPanel/>);

      case 'unknown':
        return (null);

      default:
        return (null);
    }
  }

  inactiveSessionRoute(path)
  {
    switch (this.props.sessionStatus)
    {
      case 'unknown':
        return (null);

      case 'no session':
        return (<Navigate to={{pathname: '/login', replace: true}} />);

      case 'active':
        return (<Navigate to={{pathname: '/vaultView', replace: true}} />);

      case 'change password':
        if ('/changePasswordOnLogin' === path) 
          return (<ChangePassword setSessionStatus={this.props.setSessionStatus} />);
        else
          return (<Navigate to={{pathname: '/changePasswordOnLogin', replace: true}} />);

      case 'session limit':
        if ('/sessionLimitExceeded' === path)
          return (<SessionLimitExceeded setSessionStatus={this.props.setSessionStatus} />);
        else
          return (<Navigate to={{pathname: '/sessionLimitExceeded', replace: true}} />);

      case 'auth code':
        if ('/enterAuthCode' === path)
          return (<EnterAuthCode />);
        else
          return (<Navigate to={{pathname: '/enterAuthCode', replace: true}} />);

      default:
        return (<Navigate to={{pathname: '/login', replace: true}} />);
    }
  }

  loginRoute()
  {
    switch (this.props.sessionStatus)
    {
      case 'change password':
        return (<Navigate to={{pathname: '/changePasswordOnLogin', replace: true}} />);

      case 'session limit':
        return (<Navigate to={{pathname: '/sessionLimitExceeded', replace: true}} />);

      case 'no session':
        return (<Login />);

      case 'unknown':
        return (null);

      case 'active':
        return (<Navigate to={{pathname: '/vaultView', replace: true}} />);

      case 'auth code':
        return (<Navigate to={{pathname: '/enterAuthCode', replace: true}} />);

      default:
        return (<Navigate to={{pathname: '/login', replace: true}} />);
    }
  }

  noSessionRoute({component: Component})
  {
    switch (this.props.sessionStatus)
    {
      case 'no session':
      case 'unknown':
        return (<Component setSessionStatus={this.props.setSessionStatus} />);

      case 'active':
        return (<Navigate to={{pathname: '/vaultView', replace: true}} />);

      default:
        return (<Navigate to={{pathname: '/login', replace: true}} />);
    }
  }

  render()
  { 
    return(  
      <div>
        <Routes>
          <Route path='/acceptInvitation' element={this.noSessionRoute({component: Signup})} />
          <Route path='/apiErrorLog' element={this.administratorRoute({component: ApiErrorLog})} />
          <Route path='/changePasswordOnLogin' element={this.inactiveSessionRoute('/changePasswordOnLogin')} />
          <Route path='/confirmSignup' element={this.noSessionRoute({component: ConfirmSignup})} />
          <Route path='/emailControlPanel' element={this.administratorRoute({component: EmailControlPanel})} />
          <Route path='/enterAuthCode' element={this.inactiveSessionRoute('/enterAuthCode')} /> 
          <Route path='/gmailAuthReply' element={this.gmailReplyRoute()} />
          <Route path='/login' element={this.loginRoute()} />
          <Route path='/keywords' element={this.activeSessionRoute({component: KeywordGroups})} />
          <Route path='/manageProfile' element={this.activeSessionRoute({component: UserManagement})} />
          <Route path='/mfaLogin' element={this.noSessionRoute({component: MfaLogin})} />
          <Route path='/objectTypes' element={this.administratorRoute({component: ObjectTypes})} />
          <Route path='/pathParser' element={this.activeSessionRoute({component: PathParser})} />
          <Route path='/recoverAccount' element={this.noSessionRoute({component: RecoverAccount})} />
          <Route path='/register' element={this.noSessionRoute({component: Signup})} />
          <Route path='/resetPassword' element={this.noSessionRoute({component: ResetPassword})} />
          <Route path='/secureWeblink' element={this.secureWeblinkRoute()} />
          <Route path='/sessionLimitExceeded' element={this.inactiveSessionRoute('/sessionLimitExceeded')} />
          <Route path='/supportInfo' element={this.activeSessionRoute({component: SupportInfo})} />
          <Route path='/userAdmin' element={this.administratorRoute({component: UserAdmin})} />
          <Route path='/userStreamingHistory' element={this.activeSessionRoute({component: UserStreamingHistory})} />
          <Route path='/userGatewayHistory' element={this.activeSessionRoute({component: UserGatewayHistory})} />
          <Route path='/vaultView' element={this.activeSessionRoute({component: VaultView})} />
          <Route path='/websiteAdmin' element={this.administratorRoute({component: WebsiteAdmin})} />

          <Route path='' element={this.activeSessionRoute({component: VaultView})} />
          <Route path='*' element={this.unknownRoute()} />
        </Routes>        
      </div>
    );
  }

  secureWeblinkRoute()
  {
    return (<SecureWeblink />);
  }

  unknownRoute()
  {
    return (<Navigate to={{pathname: '/login', replace: true}}/>);
  }
}

AppRoutes.contextType = AppContext;

export default AppRoutes
